import { AionCta } from "@/components/site/AionCta";
import { OG_IMAGES } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";
import { episodes } from "@/lib/podcast-episodes";

const TITLE = "Figuring Out Podcast Guests — Full Guest List";
const DESC =
  "Every Figuring Out podcast guest with Raj Shamani — cricketers, founders, actors, investors and world leaders — with episode numbers, topics and links to watch.";
const URL = "https://rajshamani.life/figuring-out-podcast-guests";
const OG = OG_IMAGES.podcast;

const faq = [
  {
    q: "Who are the biggest guests on the Figuring Out podcast?",
    a: "Figuring Out has hosted cricketers, film actors, founders, investors and public figures. The full guest list on this page is generated from the published episode archive, so it always reflects the latest releases.",
  },
  {
    q: "How does Raj Shamani choose podcast guests?",
    a: "Guests are chosen for firsthand operating experience rather than follower count — people who have actually built, led or lived through the subject being discussed. Anyone can pitch through the Be a Guest page.",
  },
  {
    q: "How can I be a guest on Figuring Out?",
    a: "You can pitch yourself through the Be a Guest page on this site. The strongest pitches include a specific topic, a concrete story only you can tell, and evidence of the work behind it.",
  },
  {
    q: "Where can I watch Figuring Out episodes?",
    a: "Full episodes are on YouTube, with audio versions on Spotify and Apple Podcasts. Every episode page here embeds the video alongside a summary, key takeaways and transcript highlights.",
  },
];

export const Route = createFileRoute("/figuring-out-podcast-guests")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Figuring Out podcast guests", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#guestlist`,
          name: "Figuring Out podcast guest list",
          itemListElement: episodes.map((e, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: e.g,
            url: `https://rajshamani.life/podcast/${e.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: GuestsPage,
});

function GuestsPage() {
  return (
    <div>
      <PageBreadcrumb current="Figuring Out Podcast Guests" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Users size={14} /> The guest list
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Figuring Out podcast <span className="text-gradient-gold">guests</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Every guest who has sat across from Raj Shamani on{" "}
            <Link to="/raj-shamani-podcast" className="text-gold underline underline-offset-4">
              Figuring Out
            </Link>
            , with the episode number, the subject of the conversation and a link to the full
            summary, takeaways and video.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Full guest list</h2>
        <ul className="grid gap-3 md:grid-cols-2">
          {episodes.map((e) => (
            <li key={e.slug}>
              <Link
                to="/podcast/$slug"
                params={{ slug: e.slug }}
                className="block rounded-xl border border-border bg-card p-5 hover:border-gold/50 transition"
              >
                <div className="text-[11px] uppercase tracking-wide text-gold">{e.n}</div>
                <div className="font-bold mt-1">{e.g}</div>
                <p className="text-sm text-muted-foreground leading-relaxed mt-1">{e.t}</p>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">What the guests have in common</h2>
        <div className="space-y-5 text-muted-foreground leading-relaxed max-w-3xl">
          <p>
            Figuring Out books guests for operating experience, not audience size. The people who
            appear on the show have usually run something difficult — a team, a company, a career in
            public — and can describe the decisions behind it in specifics rather than slogans.
          </p>
          <p>
            That is why the guest list mixes categories that rarely share a stage: cricketers next to
            D2C founders, film actors next to investors, policy figures next to educators. The
            through-line is the question Raj keeps asking — how did this actually work, step by step.
          </p>
          <p>
            If you want to appear on the show, the{" "}
            <Link to="/guest" className="text-gold underline underline-offset-4">
              Be a Guest
            </Link>{" "}
            page explains what a strong pitch looks like. For the businesses behind the show, see{" "}
            <Link to="/raj-shamani-business" className="text-gold underline underline-offset-4">
              Raj Shamani&apos;s business
            </Link>{" "}
            and{" "}
            <Link to="/house-of-x" className="text-gold underline underline-offset-4">
              House of X
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/figuring-out-podcast-guests" />

      <RelatedPages pages={["aion", "podcast", "guest", "about", "book", "faq"]} />
    </div>
  );
}
